import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { IotMapFacade } from '@iot-platform/data-access/maps';
import { StringUtils } from '@iot-platform/iot-platform-utils';
import { DevicesService } from '../../services/devices.service';
import { DeviceVariablesService } from '../../services/device-variables.service';
import { TagCategory } from '@iot-platform/models/common';
import {
  Device,
  DeviceReplacePreviewItem,
  DeviceVariable,
  I4BBulkOperationApiResponse,
  I4BBulkOperationApiResponseStatuses,
  SiteType
} from '@iot-platform/models/i4b';
import { NotificationService } from '@iot-platform/notification';
import { TranslateService } from '@ngx-translate/core';
import { FavoriteViewsActions } from '@iot-platform/data-access/favorite-views';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { get } from 'lodash';
import { catchError, concatMap, exhaustMap, filter, from, map, mergeMap, of, scan, startWith, switchMap, tap } from 'rxjs';
import { DevicesActions } from '../actions/devices.actions';
import { DevicesFacade } from '../facades/devices.facade';
import { NavigationLoggerService } from '@iot-platform/core';

const loadDeviceById$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.loadDeviceById),
      concatMap(({ deviceId }) =>
        devicesService.getById(deviceId).pipe(
          map((device: Device) => DevicesActions.loadDeviceByIdSuccess({ device })),
          catchError((error) => of(DevicesActions.loadDeviceByIdFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const loadDeviceSilently$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.getOneSilently),
      concatMap(({ id }) =>
        devicesService.getById(id).pipe(
          map((device: Device) => DevicesActions.getOneSilentlySuccess({ device })),
          catchError((error) => of(DevicesActions.getOneSilentlyFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const selectDevice$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.selectDevice),
      concatMap(({ device }) =>
        devicesService.saveTableState(device).pipe(
          map((selectedDevice: Device) => DevicesActions.selectDeviceSuccess({ device: selectedDevice })),
          catchError((error) => of(DevicesActions.selectDeviceFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const updateDevice$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.updateDevice),
      concatMap(({ device }) =>
        devicesService.updateDevice(device).pipe(
          map((updatedDevice: Device) => DevicesActions.updateDeviceSuccess({ device: updatedDevice })),
          catchError((error) => of(DevicesActions.updateDeviceFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const moveDevices$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.moveDevices),
      exhaustMap(({ devices }) =>
        from(devices).pipe(
          mergeMap((device) => devicesService.updateDevice(device).pipe(catchError(() => of(null))), 10),
          scan(
            (acc, result) => {
              acc.results.push(result);
              acc.completed++;
              return acc;
            },
            { results: [] as (Device | null)[], completed: 0, total: devices.length }
          ),
          map((state) => {
            const percent = Math.round((state.completed / state.total) * 100);
            if (state.completed < state.total) {
              return DevicesActions.updateBulkProgress({ progress: Math.min(percent, 99) });
            }
            const movedDevices = state.results.filter((r): r is Device => r !== null);
            return DevicesActions.moveDevicesSuccess({ devices: movedDevices, failedCount: state.results.length - movedDevices.length });
          }),
          startWith(DevicesActions.updateBulkProgress({ progress: 0 })),
          catchError((errors) => of(DevicesActions.moveDevicesFailure({ errors })))
        )
      )
    ),
  { functional: true }
);

const moveDevicesSuccess$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService), translateService = inject(TranslateService)) =>
    actions$.pipe(
      ofType(DevicesActions.moveDevicesSuccess),
      tap(({ devices, failedCount }) => {
        if (failedCount === 0) {
          notificationService.displaySuccess(translateService.instant('IOT_DICTIONARY.MOVE_DEVICES_PARTIAL_SUCCESS', { count: devices.length }) as string);
        } else if (devices.length > 0) {
          notificationService.displayPartialSuccess({
            successMessage: translateService.instant('IOT_DICTIONARY.MOVE_DEVICES_PARTIAL_SUCCESS', { count: devices.length }),
            failureMessage: translateService.instant('IOT_DICTIONARY.MOVE_DEVICES_PARTIAL_FAILED', { count: failedCount })
          });
        } else {
          notificationService.displayError('IOT_DICTIONARY.MOVE_DEVICES_FAILURE');
        }
      })
    ),
  { functional: true, dispatch: false }
);

const moveDevicesFailure$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(DevicesActions.moveDevicesFailure),
      tap(() => notificationService.displayError('IOT_DICTIONARY.MOVE_DEVICES_FAILURE'))
    ),
  { functional: true, dispatch: false }
);

const replaceDevice$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService), router = inject(Router)) =>
    actions$.pipe(
      ofType(DevicesActions.replaceDevice),
      concatMap(({ newDevice, oldDevice, newDeviceStatus, oldDeviceStatus }) =>
        devicesService.replaceDevice(oldDevice?.id, newDevice?.id, oldDeviceStatus, newDeviceStatus).pipe(
          map((response: DeviceReplacePreviewItem[]) => {
            router.navigate(['/devices', newDevice?.id]);
            return DevicesActions.replaceDeviceSuccess({ response });
          }),
          catchError((error) => of(DevicesActions.replaceDeviceFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const activateDevice$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.activateDevice),
      concatMap(({ device }) =>
        devicesService.activateDevice(device).pipe(
          map((activattedDevice: Device) => DevicesActions.activateDeviceSuccess({ device: activattedDevice })),
          catchError((error) => of(DevicesActions.activateDeviceFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const activateDeviceSuccess$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesFacade = inject(DevicesFacade)) =>
    actions$.pipe(
      ofType(DevicesActions.activateDeviceSuccess),
      tap(({ device }) => devicesFacade.updateDeviceInCurrentGrid(device))
    ),
  { functional: true, dispatch: false }
);

// TODO: TO REFACTOR
const resetDevice$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.resetDevice),
      concatMap(({ device }) =>
        devicesService.resetDevice(device).pipe(
          switchMap((resetedDevice: Device) => [
            DevicesActions.resetDeviceSuccess({ device: resetedDevice }),
            DevicesActions.updateDeviceSuccess({ device: resetedDevice }),
            DevicesActions.loadDeviceVariables({ deviceId: resetedDevice.id as string }),
            DevicesActions.loadDeviceTags({ deviceId: resetedDevice.id as string })
          ]),
          catchError((error) => of(DevicesActions.resetDeviceFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const deleteDevice$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.deleteDevice),
      concatMap(({ device }) =>
        devicesService.delete(device).pipe(
          map((deletedDevice: Device) => DevicesActions.deleteDeviceSuccess({ device: deletedDevice })),
          catchError((error) => of(DevicesActions.deleteDeviceFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const deleteDeviceSuccess$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), router = inject(Router), navigationLoggerService = inject(NavigationLoggerService)) =>
    actions$.pipe(
      ofType(DevicesActions.deleteDeviceSuccess),
      tap(({ device }) => {
        navigationLoggerService.markAsRemoved(router.url);
        router.navigate([device.site.type === SiteType.STOCK ? '/stocks' : '/sites', device.site.id]);
      })
    ),
  { functional: true, dispatch: false }
);

const sendCommand$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.sendCommand),
      concatMap(({ device, command }) =>
        devicesService.sendCommand(device.id as string, command).pipe(
          switchMap(() => [DevicesActions.sendCommandSuccess(), DevicesActions.loadDeviceByIdSuccess({ device: { ...device } })]),
          catchError((error) => of(DevicesActions.sendCommandFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const bulkSendCommand$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.bulkSendCommand),
      concatMap(({ devicesIds, command }) =>
        devicesService.bulkSendCommand(devicesIds, command.command).pipe(
          map((response: I4BBulkOperationApiResponse) => DevicesActions.bulkSendCommandSuccess({ response })),
          catchError((error) => of(DevicesActions.bulkSendCommandFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const loadDeviceTags$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.loadDeviceTags),
      concatMap(({ deviceId }) =>
        devicesService.getTagsByDeviceId(deviceId).pipe(
          map((tags: TagCategory[]) => DevicesActions.loadDeviceTagsSuccess({ tags })),
          catchError((error) => of(DevicesActions.loadDeviceTagsFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const updateDeviceTags$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.updateDeviceTags),
      concatMap(({ deviceId, tags }) =>
        devicesService.putTagsByDeviceId(deviceId, tags).pipe(
          map((response: TagCategory[]) => DevicesActions.updateDeviceTagsSuccess({ tags: response })),
          catchError((error) => of(DevicesActions.updateDeviceTagsFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const bulkOperationOnTagByDevicesIds$ = createEffect(
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.bulkOperationOnTag),
      concatMap((action) =>
        devicesService.bulkOperationOnTag(action.bulkOperationType, action.devicesIds, action.tagLabelId).pipe(
          map((response: I4BBulkOperationApiResponse) =>
            DevicesActions[`bulk${StringUtils.capitalizeFirstCharacter(action.bulkOperationType)}Tag-`]({ response })
          ),
          catchError((error) => of(DevicesActions[`bulk${StringUtils.capitalizeFirstCharacter(action.bulkOperationType)}TagFailure`]({ error })))
        )
      )
    ),
  { functional: true }
);

const loadDeviceVariables$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), deviceVariablesService = inject(DeviceVariablesService)) =>
    actions$.pipe(
      ofType(DevicesActions.loadDeviceVariables),
      concatMap(({ deviceId }) =>
        deviceVariablesService.getDeviceVariables(deviceId).pipe(
          map((deviceVariables: DeviceVariable[]) => DevicesActions.loadDeviceVariablesSuccess({ deviceVariables })),
          catchError((error) => of(DevicesActions.loadDeviceVariablesFailure({ error })))
        )
      )
    ),
  { functional: true }
);
// TODO remove (handled in device-variables store)
const deleteDeviceVariables$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), deviceVariablesService = inject(DeviceVariablesService)) =>
    actions$.pipe(
      ofType(DevicesActions.deleteDeviceVariables),
      concatMap(({ deviceVariables }) =>
        deviceVariablesService.bulkDeleteDeviceVariables(deviceVariables.map((dV) => dV.id)).pipe(
          switchMap((response: I4BBulkOperationApiResponse) => [
            DevicesActions.deleteDeviceVariablesSuccess({ response }),
            DevicesActions.loadDeviceVariables({ deviceId: deviceVariables[0].device.id as string })
          ]),
          catchError((error) => of(DevicesActions.deleteDeviceVariablesFailure({ error })))
        )
      )
    ),
  { functional: true }
);

// TODO remove (handled in device-variables store)
const resetDeviceVariablesLastValues$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), deviceVariablesService = inject(DeviceVariablesService)) =>
    actions$.pipe(
      ofType(DevicesActions.resetDeviceVariablesLastValues),
      concatMap(({ deviceVariables }) =>
        deviceVariablesService.bulkResetDeviceVariablesLastValues(deviceVariables.map((dV) => dV.id)).pipe(
          switchMap((response: I4BBulkOperationApiResponse) => [
            DevicesActions.resetDeviceVariablesLastValuesSuccess({ response }),
            DevicesActions.loadDeviceVariables({ deviceId: deviceVariables[0].device.id as string })
          ]),
          catchError((error) => of(DevicesActions.resetDeviceVariablesLastValuesFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const displaySuccessAfterBulkActions$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(
        DevicesActions.bulkSendCommandSuccess,
        DevicesActions['bulkAddTag-'],
        DevicesActions['bulkRemoveTag-'],
        DevicesActions['bulkReplaceTag-'],
        DevicesActions.deleteDeviceVariablesSuccess,
        DevicesActions.resetDeviceVariablesLastValuesSuccess
      ),
      tap(({ response, type }) => notificationService.displaySuccess(type + ' [ ' + I4BBulkOperationApiResponseStatuses[response.status] + ' ] '))
    ),
  { functional: true, dispatch: false }
);

const reloadDevices$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesFacade = inject(DevicesFacade)) =>
    actions$.pipe(
      ofType(
        DevicesActions.moveDevicesSuccess,
        DevicesActions.bulkSendCommandSuccess,
        DevicesActions['bulkAddTag-'],
        DevicesActions['bulkRemoveTag-'],
        DevicesActions['bulkReplaceTag-']
      ),
      tap(() => devicesFacade.reloadGrid())
    ),
  { functional: true, dispatch: false }
);

const setCurrentFavoriteView$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), mapFacade = inject(IotMapFacade)) =>
    actions$.pipe(
      ofType(FavoriteViewsActions.setCurrentFavoriteView),
      filter(({ masterView }) => masterView === 'devices'),
      tap(({ favoriteView, masterView }) =>
        mapFacade.getAll({
          concept: masterView,
          filters: get(favoriteView, 'filters', []),
          options: {
            displayMode: 'default'
          }
        })
      )
    ),
  { functional: true, dispatch: false }
);

const loadDataGuardStatus$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), devicesService = inject(DevicesService)) =>
    actions$.pipe(
      ofType(DevicesActions.loadDataGuardStatus),
      switchMap((action) =>
        devicesService.getDataGuardStatus(action.deviceId).pipe(
          map((status) => DevicesActions.loadDataGuardStatusSuccess({ status })),
          catchError((error) => of(DevicesActions.loadDataGuardStatusFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const showLoader$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(
        DevicesActions.loadDeviceById,
        DevicesActions.updateDevice,
        DevicesActions.moveDevices,
        DevicesActions.replaceDevice,
        DevicesActions.activateDevice,
        DevicesActions.resetDevice,
        DevicesActions.deleteDevice,
        DevicesActions.sendCommand,
        DevicesActions.bulkSendCommand,
        DevicesActions.loadDeviceTags,
        DevicesActions.updateDeviceTags,
        DevicesActions.bulkOperationOnTag,
        DevicesActions.deleteDeviceVariables,
        DevicesActions.resetDeviceVariablesLastValues
      ),
      tap(() => notificationService.showLoader())
    ),
  { functional: true, dispatch: false }
);

const hideLoader$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(
        DevicesActions.loadDeviceByIdSuccess,
        DevicesActions.loadDeviceByIdFailure,
        DevicesActions.updateDeviceSuccess,
        DevicesActions.updateDeviceFailure,
        DevicesActions.moveDevicesSuccess,
        DevicesActions.moveDevicesFailure,
        DevicesActions.replaceDeviceSuccess,
        DevicesActions.replaceDeviceFailure,
        DevicesActions.activateDeviceSuccess,
        DevicesActions.activateDeviceFailure,
        DevicesActions.resetDeviceSuccess,
        DevicesActions.resetDeviceFailure,
        DevicesActions.deleteDeviceSuccess,
        DevicesActions.deleteDeviceFailure,
        DevicesActions.sendCommandSuccess,
        DevicesActions.sendCommandFailure,
        DevicesActions.bulkSendCommandSuccess,
        DevicesActions.bulkSendCommandFailure,
        DevicesActions.loadDeviceTagsSuccess,
        DevicesActions.loadDeviceTagsFailure,
        DevicesActions.updateDeviceTagsSuccess,
        DevicesActions.updateDeviceTagsFailure,
        DevicesActions['bulkAddTag-'],
        DevicesActions.bulkAddTagFailure,
        DevicesActions['bulkRemoveTag-'],
        DevicesActions.bulkRemoveTagFailure,
        DevicesActions['bulkReplaceTag-'],
        DevicesActions.bulkReplaceTagFailure,
        DevicesActions.deleteDeviceVariablesSuccess,
        DevicesActions.deleteDeviceVariablesFailure,
        DevicesActions.resetDeviceVariablesLastValuesSuccess,
        DevicesActions.resetDeviceVariablesLastValuesFailure
      ),
      tap(() => notificationService.hideLoader())
    ),
  { functional: true, dispatch: false }
);

const displaySuccess$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(
        DevicesActions.updateDeviceSuccess,
        DevicesActions.replaceDeviceSuccess,
        DevicesActions.activateDeviceSuccess,
        DevicesActions.resetDeviceSuccess,
        DevicesActions.deleteDeviceSuccess,
        DevicesActions.sendCommandSuccess,
        DevicesActions.updateDeviceTagsSuccess
      ),
      tap((action: Action) => notificationService.displaySuccess(action.type))
    ),
  { functional: true, dispatch: false }
);

const displayError$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(
        DevicesActions.loadDeviceByIdFailure,
        DevicesActions.updateDeviceFailure,
        DevicesActions.replaceDeviceFailure,
        DevicesActions.activateDeviceFailure,
        DevicesActions.resetDeviceFailure,
        DevicesActions.deleteDeviceFailure,
        DevicesActions.sendCommandFailure,
        DevicesActions.bulkSendCommandFailure,
        DevicesActions.loadDeviceTagsFailure,
        DevicesActions.updateDeviceTagsFailure,
        DevicesActions.bulkAddTagFailure,
        DevicesActions.bulkRemoveTagFailure,
        DevicesActions.bulkReplaceTagFailure,
        DevicesActions.loadDeviceVariablesFailure,
        DevicesActions.resetDeviceVariablesLastValuesFailure,
        DevicesActions.deleteDeviceVariablesFailure
      ),
      tap((action: Action) => notificationService.displayError(action))
    ),
  { functional: true, dispatch: false }
);

export const DevicesEffects = {
  loadDeviceById$,
  loadDeviceSilently$,
  selectDevice$,
  updateDevice$,
  moveDevices$,
  moveDevicesSuccess$,
  moveDevicesFailure$,
  activateDevice$,
  activateDeviceSuccess$,
  deleteDevice$,
  resetDevice$,
  sendCommand$,
  bulkSendCommand$,
  loadDeviceTags$,
  updateDeviceTags$,
  bulkOperationOnTagByDevicesIds$,
  loadDeviceVariables$,
  deleteDeviceVariables$,
  resetDeviceVariablesLastValues$,
  reloadDevices$,
  setCurrentFavoriteView$,
  displaySuccessAfterBulkActions$,
  deleteDeviceSuccess$,
  replaceDevice$,
  loadDataGuardStatus$,
  showLoader$,
  hideLoader$,
  displaySuccess$,
  displayError$
};
